<template>
  <IssueLocationLayout>
    <template #headerBar>
      <HeaderBar :modifiers="['transparent', 'more-height']">
        <router-link
          v-if="backUrl"
          class="button"
          :to="backUrl"
        >
          <LeftArrow class="icon" />
        </router-link>
        <template #right>
          <div
            class="button"
            @click="cancelIssueLocation"
          >
            {{ $t('general.cancel') }}
          </div>
          <FormSubmitButton
            :disabled="disableSave"
            :isSaving="saving"
            @click="storeIssueLocation($route.params.issueId)"
          />
        </template>
      </HeaderBar>
    </template>
    <template #leftContent>
      <!-- HME group type: START -->
      <div
        v-if="operationData.is_grouped"
        class="location-row"
      >
        <p
          :aria-label="operationData.select_operation_type.title"
          class="typo-ignore typo-small-title location-type-title"
        >
          {{ operationData.select_operation_type.title }}
        </p>
        <SkeletonElement
          v-if="loadingOperationTypes"
          :height="40"
          :radius="6"
        />
        <Select
          v-if="!loadingOperationTypes && operationType"
          ref="selectType"
          v-model="selectedOperationTypeId"
          :aria-label="`${operationData.select_operation_type.title} select`"
          :items="operationType"
          :modifiers="[
            'input',
            'base',
            operationType.length < 10 ? null : 'short-dropdown',
          ]"
          :placeholder="operationData.select_operation_type.title"
          :showTotalInLabel="false"
          @click.passive="checkScroll"
        />
        <p
          v-if="selectedOperationTypeId"
          :aria-label="operationData.select_operation_type.description"
          class="typo-ignore typo-small-title"
        >
          {{ operationData.select_operation_type.description }}
        </p>
      </div>
      <!-- HME group type: END -->

      <div
        v-if="!operationData.is_grouped || operationList"
        class="location-row"
      >
        <p
          :aria-label="operationData.select_operation.title"
          class="typo-ignore typo-small-title location-type-title"
        >
          {{ operationData.select_operation.title }}
        </p>
        <SkeletonElement
          v-if="loadingOperations"
          :height="40"
          :radius="6"
        />
        <Select
          v-if="!loadingOperations && operationList"
          ref="select"
          v-model="selectedOperationId"
          :aria-label="`${operationData.select_operation.title} select`"
          :items="operationList"
          :modifiers="[
            'input',
            'base',
            operationList.length < 10 ? null : 'short-dropdown',
          ]"
          :placeholder="operationData.select_operation.title"
          @click.passive="checkScroll"
        />
        <p
          v-if="selectedOperationId"
          :aria-label="operationData.select_operation.description"
          class="typo-ignore typo-small-title"
        >
          {{ operationData.select_operation.description }}
        </p>
      </div>
    </template>
    <template #mainContent>
      <div
        class="map-container"
      >
        <Shaft3dModelToolbar
          v-if="!loadingOperation && selectedOperation && !selectedOperation?.has_pending_tile_process"
          hide3dTools
          @update:modelValue="selectedAction"
        />
        <div class="layout-regions__map-wrapper">
          <div class="layout-regions__map layout-regions__map--center">
            <LeafletMap
              v-if="!loadingOperation && selectedOperation && !selectedOperation?.has_pending_tile_process"
              ref="map"
              :diagram="selectedOperation.diagram_identifier"
              @drag="onMapMove"
              @init="onMapInit(location)"
              @mapClick="onMapClick"
            />
            <EmptyMessage
              v-else-if="!map && selectedOperation?.has_pending_tile_process"
              :message="operationData.pending_tile_process.description"
              :title="operationData.pending_tile_process.title"
            />
            <EmptyMessage
              v-else
              :message="operationData.no_selection.description"
              :title="operationData.no_selection.title"
            >
              <NoMobileAssetsSvg v-if="isHme" />
              <StationSelectSvg v-else />
            </EmptyMessage>
            <Loader
              v-if="loadingOperation"
              :modifiers="['overlay']"
            />
            <OutOfBoundsMessage v-if="map && isOutOfBounds" />
          </div>
        </div>
      </div>
    </template>
  </IssueLocationLayout>
</template>

<script setup>
import { computed, ref, onMounted, watch, nextTick, reactive } from 'vue';
import I18n from 'i18n-js';
import useAjax from '@/functions/use_ajax';
import { useRoute, useRouter } from 'vue-router';
import { useShaftV2 } from '@/functions/use_remote_object';

import FormSubmitButton from '@/components/form/form_submit_button.vue';
import HeaderBar from '@/components/generic/header_bar.vue';
import IssueLocationLayout from '@/components/shaftsV2/location/issue_location_layout.vue';
import SkeletonElement from '@/components/generic/skeleton_element.vue';
import Select from '@/components/form/select.vue';
import LeafletMap from '@/vendor/components/leaflet_map.vue';
import EmptyMessage from '@/components/generic/empty_message.vue';
import Loader from '@/components/generic/loader.vue';
import Shaft3dModelToolbar from '@/components/shaftsV2/shaft3dModel/shaft_3d_model_toolbar.vue';
import OutOfBoundsMessage from '@/components/generic/out_of_bounds_message.vue';

import { DotIcon } from '@/vendor/leaflet_mods';
import LeftArrow from '@/images/arrow-left.svg';
import StationSelectSvg from '@/images/station-select.svg';
import NoMobileAssetsSvg from '@/images/no-mobile-assets.svg';

import {
  latLngToGeoJSON,
  latLngToRgeo,
  rgeoToLatLng,
  geoJSONPolygonToLatLngs,
  rgeoPolygonToGeoJSON,
} from '@/functions/geospatial_conversions';
import { screenAtLeast } from '@/functions/screen_width';
import regionPolygonOptions from '@/functions/region_polygon_options';
import { useIssueLocationStore } from '@/stores/shafts/issueLocationStore';
import { useIssuesStore } from '@/stores/issues/issuesStore';

const PointIcon = DotIcon.extend({
  options: {
    className: 'map-modal__point',
    iconSize: [9, 9],
    iconAnchor: [5, 5],
  },
});

const props = defineProps({
  organisationId: { type: String, required: true },
  shaftId: { type: String, required: true },
  inspectionId: { type: String, required: false, default: null },
  issueType: { type: String, required: false, default: null },
  /** Use a specific route for the header nav back button */
  issueNavBackRoute: { type: String, required: false, default: null },
});

const issueStore = useIssuesStore();

const route = useRoute();
const router = useRouter();
const ajax = useAjax();
const currentShaft = useShaftV2({
  organisationId: props.organisationId,
  shaftId: props.shaftId,
});
const operationType = ref(null);
const operationTypeList = ref(null);
const operationList = ref(null);
const operationRegions = ref(null);
const selectType = ref();
const select = ref();
const selectedOperationTypeId = ref(null);
const selectedOperationType = ref(null);
const selectedOperationId = ref(null);
const selectedOperation = ref(null);
const saving = ref(false);
const loadingOperation = ref(false);
const loadingOperations = ref(false);
const loadingOperationTypes = ref(false);
const issueLocation = ref(null);
const shaftIssueLocationStore = useIssueLocationStore();

const map = ref(null);
const location = ref(null);
const currentValue = ref(null);
let marker = null;
const isHme = ref([
  'shaftV2IssueLocationHme',
  'shaftV2IssueLocationHmes',
  'shaftV2IssuesLocationTypeHme',
  'shaftV2NewIssueLocationHmes'
].includes(route?.name));
const isOutOfBounds = ref(false);

const operationData = reactive(setData());

function setData() {
  const shortBaseUrl = `/organisations/${props.organisationId}/shafts_v2`;
  const baseUrl = `${shortBaseUrl}/shafts/${props.shaftId}`;
  if (isHme.value) {
    const baseString = 'shafts.group.heavy_mobile_equipment';
    return {
      operation_id: route.params.heavyMobileEquipmentId,
      operation_id_text: 'heavyMobileEquipmentId',
      operations: `${baseUrl}/heavy_mobile_equipments`,
      regions: 'heavy_mobile_equipment_regions',
      name: 'heavy_mobile_equipment',
      id_text: 'heavy_mobile_equipment_id',
      location: 'ShaftsV2::HeavyMobileEquipmentLocation',
      select_operation: {
        title: I18n.t(`${baseString}.select_equipment`),
        description: I18n.t(`${baseString}.select_equipment_description`),
      },
      pending_tile_process: {
        title: I18n.t(`${baseString}.diagram_loading_title`),
        description: I18n.t(`${baseString}.diagram_loading_description`),
      },
      no_selection: {
        title: I18n.t(`${baseString}.no_hme_selection.title`),
        description: I18n.t(`${baseString}.no_hme_selection.message`),
      },
      // Only HME
      is_grouped: true,
      operation_types: `${shortBaseUrl}/heavy_mobile_equipment_types`,
      select_operation_type: {
        title: I18n.t(`${baseString}.select_equipment_type`),
        description: I18n.t(`${baseString}.select_equipment_type_description`),
      },
    };
  } else {
    const baseString = 'shafts.group.stations';
    return {
      operation_id: route.params.stationId,
      operation_id_text: 'stationId',
      operations: `${baseUrl}/stations`,
      regions: 'station_regions',
      name: 'station',
      id_text: 'station_id',
      location: 'ShaftsV2::StationLocation',
      select_operation: {
        title: I18n.t(`${baseString}.select_station`),
        description: I18n.t(`${baseString}.select_station_description`),
      },
      pending_tile_process: {
        title: I18n.t(`${baseString}.diagram_loading_title`),
        description: I18n.t(`${baseString}.diagram_loading_description`),
      },
      no_selection: {
        title: I18n.t(`${baseString}.no_station_selection.title`),
        description: I18n.t(`${baseString}.no_station_selection.message`),
      },
      is_grouped: false,
    };
  }
}

/**
 * Disable the save button if the location is not set
 * and we some how make it to the HME set location page though HME is disabled
 */
const disableSave = computed(() => {
  return !isHme.value ? !issueLocation.value : !issueLocation.value || !currentShaft.value?.policy?.heavy_mobile_equipment_enabled;
});

// Checks if a specific route was passed in via props
const hasIssueNavBackRoute = computed(() => {
  return props.issueNavBackRoute !== null;
});

// Default back route - to inspections
const defaultNavBackRoute = computed(() => {
  if (!route.params.issueId || route.params.issueId === 'null' || route.params.issueId === 'undefined') return { name: 'newShaftV2Issue' };
  return { name: 'shaftV2Issue', params: { issueId: route.params.issueId }};
});

// Set back route based on props
const backUrl = computed(() => {
  return hasIssueNavBackRoute.value ? { name: props.issueNavBackRoute } : defaultNavBackRoute.value;
});

// Needs to reload some data when a different panel is selected
watch(() => route.params, () => {
  selectedOperation.value = null;
  issueLocation.value = false;
  isHme.value = route?.name.includes('shaftV2IssueLocationHme');
  Object.assign(operationData, setData());
  loadOperations();
});

watch(selectedOperationTypeId, async(value) => operationList.value = await prepareOperationList(value));

watch(selectedOperationId, async(value) => selectedOperation.value = await loadOperation(value));

onMounted(async () => {
  await loadOperations();
  if (shaftIssueLocationStore.location && shaftIssueLocationStore.location[operationData.name]) {
    location.value = shaftIssueLocationStore.location;
    const operationLocation = location.value[operationData.name];
    const selectedEquipmentId = operationLocation[operationData.id_text];
    preselectFields(selectedEquipmentId);
  } else if (route.params[operationData.operation_id_text]) {
    const selectedEquipmentId = route.params[operationData.operation_id_text];
    preselectFields(selectedEquipmentId);
  }
});

async function preselectFields(id) {
  // Preselect the type
  let foundHmeId = null;
  operationData.is_grouped && Object.keys(operationTypeList.value).forEach((key) => {
    const foundEquipment = Object.values(operationTypeList.value[key]).find(equipment => equipment.id === id);
    if (foundEquipment) {
      foundHmeId = foundEquipment?.heavy_mobile_equipment_type_id;
      foundHmeId && (selectedOperationTypeId.value = foundHmeId);
    } else if (!foundHmeId) {
      /**
       * If the HME id is false then the equipment has no type
       * 'heavy_mobile_equipment' is the group for equipments without a type
       */
      selectedOperationTypeId.value = 'heavy_mobile_equipment';
    }
  });
  await nextTick();
  // Preselect the operation
  selectedOperationId.value = id;
}

function prepareOperationList(typeId) {
  selectedOperationId.value = null;
  let operationListNew = [];

  if (typeId === 'view_all') {
    Object.entries(operationTypeList.value).forEach((group) => {
      operationListNew.push(...group[1]);
    });
  } else {
    selectedOperationType.value = operationType.value.find(type => type.value === typeId);
    const labelOrValue = typeId === 'heavy_mobile_equipment' ? 'value' : 'label';
    const selectedType = selectedOperationType.value && selectedOperationType.value[labelOrValue];
    operationListNew = operationTypeList.value[selectedType];
  }

  return operationListNew?.map((item) => ({
    label: item.name,
    value: item.id,
    diagram: item.diagram_identifier,
  })) || null;
}

async function loadOperation(operationId) {
  if (operationId) {
    loadingOperation.value = true;
    let { ok, data } = await ajax(`${operationData.operations}/${operationId}`);

    if (ok) {
      // TODO: find a proper way to handle the delay
      setTimeout(() => {
        loadingOperation.value = false;
      }, 0);
      marker = null;
      return data;
    }
  }
}

async function loadOperationTypes() {
  if (route.params.shaftId) {
    loadingOperationTypes.value = true;
    let { ok, data } = await ajax(operationData.operation_types);

    if (ok) {
      operationType.value = data?.map((item) => ({
        label: item.name,
        value: item.id,
        total: `${operationTypeList.value[item.name]?.length || 0}`
      }));
      // Add extra types to the list
      let totalEquipment = 0;
      Object.entries(operationTypeList.value).forEach((group) => totalEquipment += group[1]?.length || 0);
      operationType.value.unshift(
        { label: I18n.t('shafts.configuration.heavy_mobile_equipment.view_all'), value: 'view_all', total: `${totalEquipment}` },
        { label: I18n.t('shafts.configuration.heavy_mobile_equipment.no_type'), value: 'heavy_mobile_equipment', total: `${operationTypeList.value['heavy_mobile_equipment']?.length || 0}` }
      );
      loadingOperationTypes.value = false;
    }
  }
}

async function loadOperations() {
  operationList.value = null;
  if (route.params.shaftId) {
    loadingOperations.value = true;
    let { ok, data } = await ajax(operationData.operations);

    if (ok) {
      operationData.is_grouped && (operationTypeList.value = data);
      !operationData.is_grouped && (operationList.value = data?.map((item) => ({
        label: item.name,
        value: item.id,
        diagram: item.diagram_identifier
      })));
      operationData.is_grouped && await loadOperationTypes();
      loadingOperations.value = false;
    }
  }
}

async function loadOperationRegions() {
  if (selectedOperationId.value) {
    let { ok, data } = await ajax(`${operationData.operations}/${selectedOperationId.value}/${operationData.regions}`);

    if (ok) {
      return data.regions;
    }
  }
}

function checkScroll() {
  if (selectType.value && !loadingOperationTypes.value && operationType.value) {
    selectType.value.dropdownContainerRef.scrollTop = 0;
  }
  if (!selectedOperationId.value && !loadingOperations.value && operationList.value) {
    select.value.dropdownContainerRef.scrollTop = 0;
  }
}

async function onMapInit(locationData) {
  isOutOfBounds.value = false;
  operationRegions.value = await loadOperationRegions();
  if (operationRegions.value) {
    operationRegions.value.forEach(loadRegion);
  }

  const operation = locationData && locationData[operationData.name];
  const locationId = operation && operation[operationData.id_text];
  const isSameOperation = locationId === selectedOperationId.value;
  const newLocationData = operation && operation?.location;
  if (newLocationData && isSameOperation) {
    let location = rgeoToLatLng(newLocationData);
    setMarker(...location);
    map.value.goTo(location, 18);
  } else if (!screenAtLeast('medium')) {
    let { lat, lng } = map.value.getCenter();
    setMarker(lat, lng);
  }
}

function loadRegion(region) {
  if (!region || !map.value) return;
  let geoJSON = rgeoPolygonToGeoJSON(region.bounds);
  let latLngs = geoJSONPolygonToLatLngs(geoJSON);
  let polygon = map.value.addPolygon(
    latLngs,
    regionPolygonOptions(region.region_type)
  );
  let regionObject = {
    ...region,
    geoJSON,
    latLngs,
    polygon,
  };
  polygon.region = regionObject;
}

function onMapClick({ latlng: { lat, lng } }) {
  location.value = null;
  issueLocation.value = true;
  if (screenAtLeast('medium')) {
    setMarker(lat, lng);
  }
}

function onMapMove([lat, lng]) {
  if (!screenAtLeast('large')) {
    setMarker(lat, lng);
  }
}

function setMarker(lat, lng) {
  map.value.enableCrosshair(lat, lng);
  if (!marker) {
    marker = map.value.addMarker([lat, lng], { icon: new PointIcon() });
  } else {
    marker.setLatLng([lat, lng]);
  }
  isOutOfBounds.value = false;
  currentValue.value = [lat, lng];
}

function storeIssueLocation(issueId) {
  if (validateIssuePoint()) return;

  const newPoint = currentValue.value ? latLngToRgeo(currentValue.value) : null;

  if (!!issueId && issueId !== 'null' && issueId !== 'undefined') {
    updateActivity({
      issue: {
        location_type: operationData.location,
        location_attributes: {
          id: shaftIssueLocationStore.formData?.location.id,
          location: newPoint,
          [operationData.id_text]: selectedOperation.value.id,
          direct_region_id: null,
        },
      },
    });
  } else {
    shaftIssueLocationStore.$patch({
      location: {
        type: operationData.name,
        diagram_identifier: selectedOperation.value.diagram_identifier,
        [operationData.name]: {
          location: newPoint,
          [operationData.id_text]: selectedOperation.value.id,
          direct_region_id: null,
          region: null,
          name: selectedOperation.value.name,
        }
      },
    });

    router.push(backUrl.value);
  }
}

function cancelIssueLocation() {
  if (selectedOperationTypeId.value || selectedOperationId.value) {
    // If we have types we should clear the operationList data
    operationData.is_grouped && (operationList.value = null);
    selectedOperationTypeId.value = null;
    selectedOperationId.value = null;
    selectedOperation.value =  null;
    issueLocation.value = false;
    currentValue.value = null;
    isOutOfBounds.value = false;
    marker = null;
  } else {
    router.push(backUrl.value);
  }
}

/**
 *
 * Save the updated location attributes
 */
async function updateActivity(attributes) {
  if (validateIssuePoint()) return;

  saving.value = true;

  if (!route.params.issueId || route.params.issueId === 'null') return router.push(backUrl.value);

  const baseUrl = `/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}`;
  const inspectionUrl = `${baseUrl}/inspections/${props.inspectionId}`;
  const issueUrl = `${inspectionUrl}/issues/${route.params.issueId}`;
  let { ok, data } = await ajax(issueUrl, {
    method: 'PATCH',
    body: attributes,
  });

  if (ok) {
    issueStore.updateIssue(data);
    router.push(backUrl.value);
  }
  saving.value = false;
}

/**
 * Gets the selected toolbar option and
 * calls the relevant method to update the view
 */
function selectedAction(event) {
  if (event === 'reset') {
    if (currentValue.value && marker) return map.value.goTo(currentValue.value, 18);
    map.value?.resetZoom();
  }
  // TODO: undo and redo
  // if (event === 'undo') return;
  // if (event === 'redo') return;
}

function validateIssuePoint() {
  let point = latLngToGeoJSON(currentValue.value);
  isOutOfBounds.value = map.value.diagramBoundsValidation(point.coordinates);
  return isOutOfBounds.value;
}
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';
.location-row {
  &:not(:last-of-type) {
    margin-bottom: bu(3.75);
  }

  p {
    margin: 0 0 bu(2) 0;
    line-height: bu(2.5);

    &:last-child {
      margin-top: bu(2);
    }
  }
}
.map-container {
  height: 100%;
  overflow: hidden;
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
}
</style>
